/**
 * CurrentSubscription Component
 * Displays the current subscription details with actions
 */
import { useState } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import SubscriptionStatus from './SubscriptionStatus';
import CancelModal from './CancelModal';
import { ContactLink } from '@/components/contact';
import { formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import { PAYMENT_FAILED_STATUSES, type Subscription } from '@/types/billing';

export interface CurrentSubscriptionProps {
  subscription: Subscription;
  onCancelSubscription: (immediately: boolean, reason?: string) => Promise<void>;
  onResumeSubscription: () => Promise<void>;
  onManageBilling: () => void;
  isLoading?: boolean;
  /**
   * WP-BILLING-21: only the household owner manages billing (the API returns
   * 403 to members). Members see the subscription without the actions.
   */
  canManage?: boolean;
  className?: string;
}

/**
 * Format price for display
 */
function formatPrice(amount: number, currency: string = 'USD'): string {
  const dollars = amount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(dollars);
}

export default function CurrentSubscription({
  subscription,
  onCancelSubscription,
  onResumeSubscription,
  onManageBilling,
  isLoading = false,
  canManage = true,
  className,
}: CurrentSubscriptionProps) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<'cancel' | 'resume' | null>(null);

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (immediately: boolean, reason?: string) => {
    setActionLoading('cancel');
    try {
      await onCancelSubscription(immediately, reason);
      setShowCancelModal(false);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeClick = async () => {
    setActionLoading('resume');
    try {
      await onResumeSubscription();
    } finally {
      setActionLoading(null);
    }
  };

  // Determine what to show based on subscription status
  const isCanceled = subscription.status === 'canceled';
  const willCancel = subscription.cancelAtPeriodEnd && !isCanceled;
  const isTrialing = subscription.status === 'trialing';
  const isPastDue = subscription.status === 'past_due';
  // WP-BILLING-20: unpaid (Stripe's retries exhausted) is read-only access until paid.
  const isUnpaid = subscription.status === 'unpaid';
  const paymentFailed = PAYMENT_FAILED_STATUSES.includes(subscription.status);

  return (
    <>
      <Card className={cn(className)}>
        <CardHeader
          title="Current Subscription"
          description="Your subscription details and billing information"
        />

        <div className="space-y-4">
          {/* Plan and Status Row */}
          <div className="flex items-start justify-between py-3 border-b border-gray-100">
            <div>
              <div className="font-medium text-gray-900">{subscription.planName}</div>
              <div className="text-sm text-gray-500">
                {/* WP-BUG-BILLING-SYNC-2: Use planAmount/planCurrency/planInterval from backend */}
                {formatPrice(subscription.planAmount, subscription.planCurrency)}
                {' / '}
                {subscription.planInterval}
              </div>
            </div>
            <SubscriptionStatus
              status={subscription.status}
              trialEnd={subscription.trialEnd}
              cancelAt={willCancel ? subscription.currentPeriodEnd : null}
            />
          </div>

          {/* Billing Period Row */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <div className="text-sm font-medium text-gray-700">Current Period</div>
              <div className="text-sm text-gray-500">
                {/* WP-BUG-BILLING-SYNC-2: Handle null dates gracefully */}
                {subscription.currentPeriodStart && subscription.currentPeriodEnd ? (
                  `${formatDate(subscription.currentPeriodStart)} - ${formatDate(subscription.currentPeriodEnd)}`
                ) : (
                  'Syncing billing period...'
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                {willCancel
                  ? 'Access ends'
                  : isTrialing
                  ? 'Trial ends'
                  : 'Next billing'}
              </div>
              <div className="text-sm font-medium text-gray-900">
                {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : '—'}
              </div>
            </div>
          </div>

          {/* Warning messages */}
          {willCancel && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Subscription scheduled for cancellation
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    You will have access until {formatDate(subscription.currentPeriodEnd)}.
                    You can resume your subscription to keep your access.
                  </p>
                </div>
              </div>
            </div>
          )}

          {paymentFailed && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-800">
                    {isPastDue ? 'Payment past due' : 'Subscription unpaid'}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {isUnpaid
                      ? 'Premium features are paused and your data is read-only. Update your payment method to restore them.'
                      : 'Please update your payment method to avoid service interruption.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!canManage && (
            <p className="text-sm text-gray-500 pt-2">
              Billing is managed by the household owner.
            </p>
          )}

          {canManage && subscription.isCouponBased && (
            <p className="text-sm text-gray-600 pt-2">
              Your plan is managed by support.{' '}
              <ContactLink
                topic="billing"
                context="CurrentSubscription"
                className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                Contact support
              </ContactLink>{' '}
              to change it.
            </p>
          )}

          {/* Action buttons (WP-BILLING-19: a coupon plan has no Stripe portal) */}
          {canManage && !subscription.isCouponBased && (
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              variant={paymentFailed ? 'primary' : 'outline'}
              onClick={onManageBilling}
              disabled={isLoading}
            >
              {paymentFailed ? 'Update Payment Method' : 'Manage Payment Method'}
            </Button>

            {willCancel ? (
              <Button
                variant="primary"
                onClick={handleResumeClick}
                isLoading={actionLoading === 'resume'}
                disabled={isLoading}
              >
                Resume Subscription
              </Button>
            ) : (
              !isCanceled && (
                <Button
                  variant="ghost"
                  onClick={handleCancelClick}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              )
            )}
          </div>
          )}
        </div>
      </Card>

      {/* Cancel Modal */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        isLoading={actionLoading === 'cancel'}
        periodEnd={subscription.currentPeriodEnd}
      />
    </>
  );
}
